import type { BaselineStats } from '@qi/resonance-engine';
import type {
  FaceSummary,
  FacialComplexionReading,
  Goal,
  ResonancePeak,
  TcmInterpretation,
  TongueReading,
  VoiceSummary,
  WellnessScores,
} from '@qi/shared';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { ComplexionReading, ZoneColors } from '../lib/complexion.ts';

interface AuthUser {
  email: string;
}

/**
 * Scan-result payload that the Results route writes once after each completed
 * scan flow. Compare reads it back to render real before/after deltas instead
 * of fabricated fallbacks.
 */
export interface ScanOutcome {
  scanId: string;
  scores: WellnessScores;
  /** Top resonance peaks the recommendation/results UI surfaces. */
  peakFrequencyIds: string[];
  /** Six TCM scores snapshotted at scan time so Compare's TCM "before" can't
   *  drift from the wellness "before". Null when the scan carried no TCM read. */
  tcm: TcmScores | null;
  capturedAt: string;
}

/** The six numeric TCM scores, split out so the session can project a
 *  post-session "after" the same way it projects the wellness scores. */
export type TcmScores = Pick<
  TcmInterpretation,
  'balance' | 'vitality' | 'heat' | 'fluids' | 'circulation' | 'organHarmony'
>;

/** Narrow a full interpretation (or any TCM-bearing object) down to just its
 *  six scores — used to snapshot the TCM read into the scan outcome. */
export function toTcmScores(t: TcmScores | null): TcmScores | null {
  if (!t) return null;
  const { balance, vitality, heat, fluids, circulation, organHarmony } = t;
  return { balance, vitality, heat, fluids, circulation, organHarmony };
}

type Tier = 'free' | 'premium';

/** How long the one-time unlock keeps rebalancing playable. The full reading is
 *  kept forever; only the *playback* of the tones is time-boxed to this window,
 *  after which continuing requires the premium (Plus) subscription. */
export const REBALANCE_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

// Only paying grants the full reading-forever + 7-day rebalancing bundle.
// Sharing earns the lesser solfeggio-chord unlock (`unlockViaShare`) — a share
// never equals the paid unlock.
export type UnlockSource = 'paid';

interface SessionState {
  user: AuthUser | null;
  /** Billing tier. Premium unlocks the full clinical-style resonance readout.
   *  No real checkout yet — `unlockPremium` is a UI-gate stand-in until Stripe
   *  is wired, so the upsell can be demoed end-to-end. */
  tier: Tier;
  goal: Goal | null;
  /** Live face metrics from the BioScan step. Cleared when a new scan flow
   *  starts so a previous run can't bleed into the next. */
  faceSummary: FaceSummary | null;
  /** Relative TCM complexion read from the BioScan step. Per-scan; reset with
   *  the other scan inputs. */
  complexion: ComplexionReading | null;
  /** Per-zone mean colour from the user's most recent completed scan. Persists
   *  ACROSS scans (not cleared by resetScanInputs) so each new read can be
   *  compared zone-vs-own-history as a self-baseline. */
  complexionBaseline: ZoneColors | null;
  /** Downscaled JPEG data URL of the tongue capture from the Tongue step.
   *  Kept small (~320px) so persisting it to localStorage doesn't bloat. Per
   *  scan; cleared with the other scan inputs. */
  tonguePhoto: string | null;
  /** TCM tongue reading from the vision model, derived from tonguePhoto. Per
   *  scan; cleared with the other scan inputs. */
  tongueReading: TongueReading | null;
  /** LLM TCM facial-complexion read from the same photo as the tongue read.
   *  Distinct from `complexion` (the BioScan CIE-Lab measurement). Per scan;
   *  cleared with the other scan inputs. */
  faceComplexion: FacialComplexionReading | null;
  /** Combined plain-language TCM interpretation (yin/yang + what to balance),
   *  synthesised by the LLM from the same photo as the tongue/face reads. Per
   *  scan; cleared with the other scan inputs. */
  tcmInterpretation: TcmInterpretation | null;
  /** Live voice metrics from the Voice Scan step. */
  voiceSummary: VoiceSummary | null;
  /** Per-signal baseline (mean + sd) computed from the BioScan capture.
   *  Drives z-normalization of sweep deltas (SRS FR-SCO-2). */
  baseline: BaselineStats | null;
  /** Resonance peaks emitted by the Sweep step — top accepted clusters in
   *  descending score order. Empty if the sweep hasn't run yet. */
  resonancePeaks: ResonancePeak[];
  /** Most recent scan outcome — drives Results personalisation and the
   *  session-complete screen's baseline. Persisted so refreshes inside the scan
   *  flow don't erase what just landed. */
  lastScan: ScanOutcome | null;
  /** Whether the live blink/stillness/breathing numbers are shown *during*
   *  capture. Off by default: seeing the controllable metrics live makes users
   *  self-regulate (blink less, hold their breath), which skews the baseline
   *  every sweep delta is normalized against. Opt-in for users who want them. */
  showLiveReadings: boolean;
  /** Set once the user shares their results — the free viral-loop reward. It
   *  unlocks their second-priority rebalance track for this session (priority 1
   *  is always free). Distinct from `tier` / the paid unlock: it grants one
   *  extra track, not the full reading or rebalancing window. */
  shareUnlocked: boolean;
  /** ISO timestamp of the one-time reading unlock. Once set, the full reading
   *  is the user's to keep forever. Null until they pay or share to unlock.
   *  `premium` tier supersedes this (subscribers don't need a per-scan unlock). */
  readingUnlockedAt: string | null;
  /** ISO timestamp when the post-unlock rebalancing window closes. Playback is
   *  allowed until then; after it, continuing requires premium. */
  rebalanceWindowEndsAt: string | null;
  /** Which rebalance items the user has finished playing today, bucketed by
   *  local calendar day. Drives the "already played today" marker on the
   *  up-next priorities list. Resets automatically when the day rolls over. */
  rebalancePlayed: { day: string; ids: string[] };
  signIn: (user: AuthUser) => void;
  signOut: () => void;
  unlockPremium: () => void;
  /** Flip the share reward on — called after a successful results share. */
  unlockViaShare: () => void;
  /** One-time paid unlock: keep the full reading forever and open a 7-day
   *  rebalancing window. Sharing does not grant this bundle (see UnlockSource). */
  unlockReading: (source: UnlockSource) => void;
  /** Record that a rebalance item finished playing today (idempotent per day). */
  markRebalancePlayed: (id: string) => void;
  /** Admin/demo "view as" override — flips the local tier either way so the
   *  free vs premium gating can be previewed without a real plan change. */
  setTier: (tier: Tier) => void;
  setGoal: (goal: Goal) => void;
  setFaceSummary: (summary: FaceSummary | null) => void;
  setComplexion: (reading: ComplexionReading | null) => void;
  setComplexionBaseline: (zones: ZoneColors) => void;
  setTonguePhoto: (dataUrl: string | null) => void;
  setTongueReading: (reading: TongueReading | null) => void;
  setFaceComplexion: (reading: FacialComplexionReading | null) => void;
  setTcmInterpretation: (interpretation: TcmInterpretation | null) => void;
  setVoiceSummary: (summary: VoiceSummary | null) => void;
  setBaseline: (baseline: BaselineStats | null) => void;
  setResonancePeaks: (peaks: ResonancePeak[]) => void;
  setLastScan: (outcome: ScanOutcome) => void;
  setShowLiveReadings: (show: boolean) => void;
  resetScanInputs: () => void;
}

/** Local calendar day key (YYYY-MM-DD) used to bucket "played today" state so it
 *  rolls over at the user's midnight, not UTC. */
function dayKey(d = new Date()): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Stable empty reference so the selector doesn't return a fresh array each call
// (which would trip zustand's getSnapshot caching and loop the render).
const NO_PLAYED: string[] = [];

/** Ids of rebalance items finished today — empty once the day rolls over. */
export function selectPlayedTodayIds(s: SessionState): string[] {
  return s.rebalancePlayed.day === dayKey() ? s.rebalancePlayed.ids : NO_PLAYED;
}

/** Whether the user may play rebalancing tones right now: premium subscribers
 *  always can; one-time unlockers can until their 7-day window closes. */
export function selectCanRebalance(s: SessionState): boolean {
  if (s.tier === 'premium') return true;
  if (!s.rebalanceWindowEndsAt) return false;
  return Date.now() < Date.parse(s.rebalanceWindowEndsAt);
}

/**
 * Client-side mirror of auth + onboarding state. Backed by localStorage so a
 * refresh keeps you "signed in." The source of truth is Better Auth — `AuthSync`
 * mirrors its session into this store so legacy `state.user` consumers work.
 */
export const useSessionStore = create<SessionState>()(
  persist(
    (set) => ({
      user: null,
      tier: 'free',
      goal: null,
      faceSummary: null,
      complexion: null,
      complexionBaseline: null,
      tonguePhoto: null,
      tongueReading: null,
      faceComplexion: null,
      tcmInterpretation: null,
      voiceSummary: null,
      baseline: null,
      resonancePeaks: [],
      lastScan: null,
      showLiveReadings: false,
      shareUnlocked: false,
      readingUnlockedAt: null,
      rebalanceWindowEndsAt: null,
      rebalancePlayed: { day: '', ids: [] },
      signIn: (user) => set({ user }),
      signOut: () => set({ user: null }),
      unlockPremium: () => set({ tier: 'premium' }),
      unlockViaShare: () => set({ shareUnlocked: true }),
      unlockReading: () => {
        const now = Date.now();
        set({
          readingUnlockedAt: new Date(now).toISOString(),
          rebalanceWindowEndsAt: new Date(now + REBALANCE_WINDOW_MS).toISOString(),
        });
      },
      markRebalancePlayed: (id) =>
        set((s) => {
          const day = dayKey();
          const ids = s.rebalancePlayed.day === day ? s.rebalancePlayed.ids : [];
          if (ids.includes(id)) return s;
          return { rebalancePlayed: { day, ids: [...ids, id] } };
        }),
      setTier: (tier) => set({ tier }),
      setGoal: (goal) => set({ goal }),
      setFaceSummary: (faceSummary) => set({ faceSummary }),
      setComplexion: (complexion) => set({ complexion }),
      setComplexionBaseline: (complexionBaseline) => set({ complexionBaseline }),
      setTonguePhoto: (tonguePhoto) => set({ tonguePhoto }),
      setTongueReading: (tongueReading) => set({ tongueReading }),
      setFaceComplexion: (faceComplexion) => set({ faceComplexion }),
      setTcmInterpretation: (tcmInterpretation) => set({ tcmInterpretation }),
      setVoiceSummary: (voiceSummary) => set({ voiceSummary }),
      setBaseline: (baseline) => set({ baseline }),
      setResonancePeaks: (resonancePeaks) => set({ resonancePeaks }),
      setLastScan: (lastScan) => set({ lastScan }),
      setShowLiveReadings: (showLiveReadings) => set({ showLiveReadings }),
      resetScanInputs: () =>
        set({
          faceSummary: null,
          complexion: null,
          tonguePhoto: null,
          tongueReading: null,
          faceComplexion: null,
          tcmInterpretation: null,
          voiceSummary: null,
          baseline: null,
          resonancePeaks: [],
        }),
    }),
    {
      name: 'qi.session',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
